import {DateOrTimeView, DateTimePicker, LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import clsx from 'clsx'
import {FormikProps, getIn} from 'formik'
import {isEmpty} from 'lodash'
import 'moment/min/locales'
import moment from 'moment/moment'
import {FC, useEffect, useMemo} from 'react'
import {LANGUAGE_KEY} from '../../config'
import {useLanguage} from '../../i18n/Metronici18n'
import {FORMAT_DATE} from '../../constants'

type Props = {
  className?: string
  labelClassName?: string | null
  label?: string | null
  required?: boolean
  formik: FormikProps<any>
  name: string
  append?: string
  disabled?: boolean
  min?: any
  views?: DateOrTimeView[]
  isLocalTime?: boolean
  dateTimeProps?: any
}

const InputDate: FC<Props> = ({
  className,
  labelClassName = null,
  label = null,
  formik,
  name,
  required = false,
  disabled = false,
  min = moment('2000-01-01'),
  views = ['year', 'month', 'day'],
  isLocalTime = false,
  dateTimeProps,
}) => {
  let fieldProps = formik.getFieldProps(name)
  let formikErrors = getIn(formik.errors, name)
  let formikTouched = getIn(formik.touched, name)
  const {language} = useLanguage()

  const localeValue = useMemo(() => {
    let lang = language
    switch (language) {
      case LANGUAGE_KEY.ZH:
        lang = 'zh-cn'
        break
    }
    return lang
  }, [language])

  useEffect(() => {
    const div = document.querySelector('.date-picker')
    if (div) {
      const input = div.querySelector('input')
      if (input) {
        input.setAttribute('name', name)
      }
    }
  }, [name])

  useEffect(() => {
    let event = {
      target: {
        name: fieldProps.name,
        value: isEmpty(getIn(formik.values, name)) ? null : getIn(formik.values, name),
      },
    }
    formik.handleChange(event)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Validate when has data date
  useEffect(() => {
    if (!formikTouched && !isEmpty(getIn(formik.values, name))) {
      formik.setFieldTouched(fieldProps.name, true)
    }
  }, [fieldProps.name, formik, formikTouched, name])

  const defaultValue = useMemo(() => {
    const value = getIn(formik.values, name)
    if (isEmpty(value)) {
      return null
    }

    if (isLocalTime) return moment(value).local()

    return moment(value)
  }, [formik.values, isLocalTime, name])

  return (
    <>
      {label && (
        <label className={clsx('form-label', labelClassName, {required: required})}>{label}</label>
      )}
      <div className={className}>
        <div className={`input-group ${formikTouched && formikErrors ? 'error-date' : ''}`}>
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            dateLibInstance={moment}
            adapterLocale={localeValue}
          >
            <DateTimePicker
              className={'date-picker'}
              sx={{
                backgroundColor: (disabled && '#eff2f5') || '#ffffff',
                input: {
                  fontSize: '13px',
                },
              }}
              minDate={isLocalTime ? moment(min).local() : min}
              disabled={disabled}
              defaultValue={defaultValue}
              onChange={(value) => {
                let event = {
                  target: {
                    name: fieldProps.name,
                    value,
                  },
                }
                formik.handleChange(event)
              }}
              slotProps={{
                textField: {
                  inputProps: {
                    readOnly: true,
                  },
                },
              }}
              views={views}
              closeOnSelect={false}
              {...dateTimeProps}
            />
            {isLocalTime && (
              <span className='date-time-timezone'>
                {formik.values[name] &&
                  moment(formik.values[name]).local().format(FORMAT_DATE.TIMEZONE)}
              </span>
            )}
          </LocalizationProvider>
        </div>
        {formikTouched && formikErrors && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block text-danger'>
              <span role='alert'>{formikErrors}</span>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export {InputDate}
